import { useEffect, useRef, useState } from 'react';

import { getAuthToken } from '../auth/session';
import { API_ORIGIN } from '../config';

export const INVENTORY_VOICE_PATH = '/api/inventory/voice';

const VOICE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

export function buildInventoryVoiceWebSocketUrl({
  apiOrigin = API_ORIGIN,
  browserLocation = typeof window !== 'undefined' ? window.location : null,
} = {}) {
  const origin = String(apiOrigin || browserLocation?.origin || '').trim();
  if (!origin) throw new Error('Needl voice connection is unavailable.');
  const url = new URL(INVENTORY_VOICE_PATH, origin.endsWith('/') ? origin : `${origin}/`);
  if (url.protocol === 'https:') url.protocol = 'wss:';
  else if (url.protocol === 'http:') url.protocol = 'ws:';
  else throw new Error('Needl voice connection must use HTTP or HTTPS.');
  return url.toString();
}

export function preferredInventoryVoiceMimeType(MediaRecorderImpl) {
  if (!MediaRecorderImpl) return '';
  if (typeof MediaRecorderImpl.isTypeSupported !== 'function') return '';
  return VOICE_MIME_TYPES.find((mimeType) => MediaRecorderImpl.isTypeSupported(mimeType)) || '';
}

export function microphoneErrorMessage(error) {
  const name = String(error?.name || '');
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone access is blocked. Allow it for Needl and try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone was found on this device.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'The microphone is busy in another app.';
  }
  return String(error?.message || 'Could not start the microphone.').slice(0, 300);
}

function stopTracks(stream) {
  for (const track of stream?.getTracks?.() || []) {
    try {
      track.stop();
    } catch {
      // Ignore a track that already ended.
    }
  }
}

export function useInventoryVoice({ onTranscript, onResult, onError } = {}) {
  const [phase, setPhase] = useState('idle');
  const [message, setMessage] = useState('');
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const socketRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const phaseRef = useRef('idle');
  const sessionRef = useRef(0);
  const completedRef = useRef(false);
  const limitTimerRef = useRef(null);
  const callbacksRef = useRef({ onTranscript, onResult, onError });

  callbacksRef.current = { onTranscript, onResult, onError };

  const updatePhase = (nextPhase, nextMessage = '') => {
    phaseRef.current = nextPhase;
    setPhase(nextPhase);
    setMessage(nextMessage);
  };

  const clearLimitTimer = () => {
    if (limitTimerRef.current) window.clearTimeout(limitTimerRef.current);
    limitTimerRef.current = null;
  };

  const releaseMedia = () => {
    clearLimitTimer();
    stopTracks(streamRef.current);
    streamRef.current = null;
    recorderRef.current = null;
  };

  const reportError = (value) => {
    const nextError = microphoneErrorMessage(value);
    completedRef.current = true;
    setError(nextError);
    updatePhase('error', nextError);
    callbacksRef.current.onError?.(nextError);
    releaseMedia();
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, 'voice command failed');
  };

  const stop = () => {
    if (phaseRef.current !== 'recording') return false;
    clearLimitTimer();
    updatePhase('stopping', 'Sending');
    const recorder = recorderRef.current;
    if (recorder?.state === 'recording' || recorder?.state === 'paused') {
      recorder.stop();
      return true;
    }
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'stop' }));
    releaseMedia();
    return true;
  };

  const start = async (context = {}) => {
    if (['requesting', 'connecting', 'recording', 'stopping', 'transcribing', 'executing'].includes(phaseRef.current)) {
      return false;
    }
    const token = getAuthToken();
    if (!token) {
      reportError(new Error('Sign in to use voice commands.'));
      return false;
    }
    const MediaRecorderImpl = typeof window !== 'undefined' ? window.MediaRecorder : null;
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!MediaRecorderImpl || !mediaDevices?.getUserMedia || typeof WebSocket === 'undefined') {
      reportError(new Error('Voice commands are not supported in this browser.'));
      return false;
    }

    const sessionId = sessionRef.current + 1;
    sessionRef.current = sessionId;
    completedRef.current = false;
    setError('');
    setTranscript('');
    updatePhase('requesting', 'Allow microphone');

    try {
      const stream = await mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      if (sessionRef.current !== sessionId) {
        stopTracks(stream);
        return false;
      }
      streamRef.current = stream;

      const preferredMimeType = preferredInventoryVoiceMimeType(MediaRecorderImpl);
      const recorder = preferredMimeType
        ? new MediaRecorderImpl(stream, { mimeType: preferredMimeType, audioBitsPerSecond: 64000 })
        : new MediaRecorderImpl(stream);
      const mimeType = String(recorder.mimeType || preferredMimeType || 'audio/webm');
      recorderRef.current = recorder;

      const socket = new WebSocket(buildInventoryVoiceWebSocketUrl());
      socket.binaryType = 'arraybuffer';
      socketRef.current = socket;
      updatePhase('connecting', 'Connecting');

      recorder.ondataavailable = (event) => {
        if (!event.data?.size) return;
        if (socket.readyState === WebSocket.OPEN) socket.send(event.data);
      };
      recorder.onerror = (event) => reportError(event?.error || new Error('Microphone recording failed.'));
      recorder.onstop = () => {
        stopTracks(streamRef.current);
        streamRef.current = null;
        clearLimitTimer();
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'stop' }));
      };

      socket.onopen = () => {
        if (sessionRef.current !== sessionId) return;
        socket.send(JSON.stringify({
          type: 'start',
          token,
          mimeType,
          context,
        }));
      };

      socket.onmessage = (event) => {
        if (sessionRef.current !== sessionId) return;
        let payload;
        try {
          payload = JSON.parse(String(event.data || ''));
        } catch {
          reportError(new Error('Needl returned an invalid voice response.'));
          return;
        }

        if (payload.type === 'ready') {
          updatePhase('recording', 'Listening');
          recorder.start(250);
          const maxDurationMs = Math.max(1000, Number(payload.maxDurationMs) || 60000);
          limitTimerRef.current = window.setTimeout(() => stop(), Math.max(1000, maxDurationMs - 250));
          return;
        }
        if (payload.type === 'status') {
          updatePhase(String(payload.phase || 'working'), String(payload.message || 'Working'));
          if (payload.phase === 'stopping' && recorder.state === 'recording') recorder.stop();
          return;
        }
        if (payload.type === 'transcript') {
          const nextTranscript = String(payload.transcript || '').trim();
          setTranscript(nextTranscript);
          callbacksRef.current.onTranscript?.(nextTranscript);
          return;
        }
        if (payload.type === 'result') {
          completedRef.current = true;
          updatePhase('complete', 'Done');
          releaseMedia();
          callbacksRef.current.onResult?.(payload);
          return;
        }
        if (payload.type === 'error') {
          reportError(new Error(payload.message || 'Voice command failed.'));
        }
      };

      socket.onerror = () => {
        if (!completedRef.current) reportError(new Error('Could not connect to Needl voice commands.'));
      };
      socket.onclose = () => {
        if (socketRef.current === socket) socketRef.current = null;
        releaseMedia();
        if (!completedRef.current && !['idle', 'error'].includes(phaseRef.current)) {
          reportError(new Error('The voice connection ended before the command finished.'));
        }
      };
      return true;
    } catch (startError) {
      if (sessionRef.current === sessionId) reportError(startError);
      return false;
    }
  };

  const cancel = () => {
    sessionRef.current += 1;
    completedRef.current = true;
    clearLimitTimer();
    const recorder = recorderRef.current;
    if (recorder?.state === 'recording' || recorder?.state === 'paused') {
      try {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      } catch {
        // Ignore a recorder that ended between the state check and stop call.
      }
    }
    releaseMedia();
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'cancel' }));
      socket.close(1000, 'voice command canceled');
    }
    setError('');
    setTranscript('');
    updatePhase('idle', '');
  };

  useEffect(() => () => {
    sessionRef.current += 1;
    completedRef.current = true;
    clearLimitTimer();
    stopTracks(streamRef.current);
    if (recorderRef.current) {
      recorderRef.current.ondataavailable = null;
      recorderRef.current.onstop = null;
    }
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'cancel' }));
      socket.close(1000, 'inventory page closed');
    }
  }, []);

  return {
    phase,
    message,
    transcript,
    error,
    isRecording: phase === 'recording',
    isBusy: ['requesting', 'connecting', 'stopping', 'transcribing', 'executing'].includes(phase),
    isActive: phase !== 'idle' || Boolean(transcript) || Boolean(error),
    start,
    stop,
    cancel,
  };
}
